/** One unit per variant, store-wide: a variant already in the cart cannot be added again, no cart line goes above quantity 1, a landed add is read back and healed where a server-side merge raised a line, and an add button whose variant the cart holds acts as remove-from-cart. */
(function () {
  if (window.__tbOnePerVariant) return;
  window.__tbOnePerVariant = true;

  /* Cart permalinks, non-browser adds and a cart read that fails all bypass this layer: the paid-order webhook's one-unit clamp, its WEBHOOK_HOLD_CLAMPED alert and the ReStyle case that alert turns into a conversation are the designed net behind it. */
  var IN_CART = "Already in your cart — one per order";
  var LIMIT = "Limit one per order";
  var REMOVE_LABEL = "Remove from cart";
  var ADDED_MSG = "Added to cart";
  var REMOVED_MSG = "Removed from cart";
  var CART_TTL = 1500; // a cart read stays authoritative this long, so a burst of clicks costs one request
  var NOTE_MS = 6000;

  var nativeFetch = window.fetch;
  if (typeof nativeFetch !== "function") return;
  nativeFetch = nativeFetch.bind(window);

  // ---------------- cart state ----------------

  var cartIds = null; // variant ids as strings, or null before the first successful read
  var readAt = 0;
  var pending = null;

  function root() {
    var r =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      "/";
    return r.charAt(r.length - 1) === "/" ? r : r + "/";
  }

  /** A read that fails resolves to an empty set rather than refusing the add: a shopper whose /cart.js is blocked can still shop, and the webhook clamp is what catches the duplicate. */
  function readCart() {
    if (pending) return pending;
    if (cartIds && Date.now() - readAt < CART_TTL)
      return Promise.resolve(cartIds);
    pending = nativeFetch(root() + "cart.js", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (cart) {
        if (cart) {
          var ids = new Set();
          (cart.items || []).forEach(function (item) {
            ids.add(String(item.variant_id));
          });
          cartIds = ids;
          readAt = Date.now();
        }
        pending = null;
        return cartIds || new Set();
      })
      .catch(function () {
        pending = null;
        return cartIds || new Set();
      });
    return pending;
  }

  function invalidate() {
    cartIds = null;
    readAt = 0;
  }

  // ---------------- request reading ----------------

  /** Shopify's Ajax Cart API paths are the contract this guard reads, so no theme markup is involved and one file holds on Prestige and on Dawn. */
  function endpoint(url) {
    var path;
    try {
      path = new URL(url, window.location.href).pathname;
    } catch (e) {
      return null;
    }
    if (/\/cart\/add(\.js)?$/.test(path)) return "add";
    if (/\/cart\/change(\.js)?$/.test(path)) return "change";
    if (/\/cart\/update(\.js)?$/.test(path)) return "update";
    return null;
  }

  function qty(value) {
    var n = parseInt(value, 10);
    return isFinite(n) && n > 0 ? n : 1;
  }

  function asJson(body) {
    if (typeof body !== "string") return null;
    var t = body.trim();
    if (t.charAt(0) !== "{" && t.charAt(0) !== "[") return null;
    try {
      return JSON.parse(t);
    } catch (e) {
      return null;
    }
  }

  function asParams(body) {
    if (
      typeof URLSearchParams === "function" &&
      body instanceof URLSearchParams
    )
      return body;
    if (typeof body === "string" && body.indexOf("=") > -1) {
      try {
        return new URLSearchParams(body);
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  /** What an add request asks for and how to rewrite it to one unit, or null when the body cannot be read. */
  function addPlan(body) {
    if (typeof FormData === "function" && body instanceof FormData) {
      var id = body.get("id");
      if (id == null) return null;
      return {
        items: [{ id: String(id), quantity: qty(body.get("quantity")) }],
        toOne: function () {
          body.set("quantity", "1");
          return body;
        },
      };
    }
    var json = asJson(body);
    if (json) {
      if (Array.isArray(json.items)) {
        return {
          items: json.items.map(function (item) {
            return { id: String(item.id), quantity: qty(item.quantity) };
          }),
          toOne: function () {
            json.items.forEach(function (item) {
              item.quantity = 1;
            });
            return JSON.stringify(json);
          },
        };
      }
      if (json.id != null)
        return {
          items: [{ id: String(json.id), quantity: qty(json.quantity) }],
          toOne: function () {
            json.quantity = 1;
            return JSON.stringify(json);
          },
        };
      return null;
    }
    var params = asParams(body);
    if (params && params.get("id") != null)
      return {
        items: [
          {
            id: String(params.get("id")),
            quantity: qty(params.get("quantity")),
          },
        ],
        toOne: function () {
          params.set("quantity", "1");
          return params;
        },
      };
    return null;
  }

  /** The absolute quantity a change request sets and how to rewrite it to one unit, or null when the body cannot be read. */
  function changePlan(body) {
    var json = asJson(body);
    if (json && json.quantity != null)
      return {
        quantity: qty(json.quantity),
        toOne: function () {
          json.quantity = 1;
          return JSON.stringify(json);
        },
      };
    var params = asParams(body);
    if (params && params.get("quantity") != null)
      return {
        quantity: qty(params.get("quantity")),
        toOne: function () {
          params.set("quantity", "1");
          return params;
        },
      };
    if (
      typeof FormData === "function" &&
      body instanceof FormData &&
      body.get("quantity") != null
    )
      return {
        quantity: qty(body.get("quantity")),
        toOne: function () {
          body.set("quantity", "1");
          return body;
        },
      };
    return null;
  }

  /** Whether an update request raises a line above one unit and how to rewrite it, or null when the body cannot be read. */
  function updatePlan(body) {
    var json = asJson(body);
    if (json && json.updates) {
      var updates = json.updates;
      var over = Array.isArray(updates)
        ? updates.some(function (v) {
            return Number(v) > 1;
          })
        : Object.keys(updates).some(function (k) {
            return Number(updates[k]) > 1;
          });
      return {
        over: over,
        toOne: function () {
          if (Array.isArray(updates))
            json.updates = updates.map(function (v) {
              return Number(v) > 1 ? 1 : v;
            });
          else
            Object.keys(updates).forEach(function (k) {
              if (Number(updates[k]) > 1) updates[k] = 1;
            });
          return JSON.stringify(json);
        },
      };
    }
    var params = asParams(body);
    if (params) {
      var lines = [];
      params.forEach(function (value, key) {
        if (key.indexOf("updates") === 0) lines.push([key, value]);
      });
      if (!lines.length) return null;
      return {
        over: lines.some(function (line) {
          return Number(line[1]) > 1;
        }),
        // rebuilt rather than set(): a cart form repeats updates[] once per line, and set() would collapse them to one
        toOne: function () {
          var next = new URLSearchParams();
          params.forEach(function (value, key) {
            next.append(
              key,
              key.indexOf("updates") === 0 && Number(value) > 1 ? "1" : value,
            );
          });
          return next;
        },
      };
    }
    return null;
  }

  /** BLOCK, ONE (send it clamped to a single unit) or null, never a line-by-line rewrite of a multi-item add — a bundle add that lost a line would check out at the wrong price. */
  function verdict(plan, ids) {
    var seen = {};
    for (var i = 0; i < plan.items.length; i++) {
      var item = plan.items[i];
      if (ids.has(item.id) || seen[item.id]) return "BLOCK";
      seen[item.id] = true;
      if (item.quantity > 1) return plan.items.length > 1 ? "BLOCK" : "ONE";
    }
    return null;
  }

  /** Shopify's own refused-add body, so the theme reports it through the path it already has for one. */
  function refusal() {
    var payload = {
      status: 422,
      message: "Cart Error",
      description: IN_CART,
      errors: IN_CART,
    };
    return new Response(JSON.stringify(payload), {
      status: 422,
      statusText: "Unprocessable Entity",
      headers: { "Content-Type": "application/json" },
    });
  }

  // ---------------- the guard ----------------

  window.fetch = function (input, init) {
    var url;
    try {
      url =
        typeof input === "string"
          ? input
          : input && input.url
            ? input.url
            : String(input);
    } catch (e) {
      return nativeFetch.apply(null, arguments);
    }
    var kind = endpoint(url);
    var method = String(
      (init && init.method) || (input && input.method) || "GET",
    ).toUpperCase();
    if (!kind || method !== "POST") return nativeFetch.apply(null, arguments);
    var body = init && init.body != null ? init.body : null;
    if (body == null) return nativeFetch.apply(null, arguments);

    var args = arguments;
    var passThrough = function () {
      return nativeFetch.apply(null, args).then(function (response) {
        invalidate();
        return response;
      });
    };
    var sendClamped = function (newBody) {
      var nextInit = {};
      for (var key in init)
        if (Object.prototype.hasOwnProperty.call(init, key))
          nextInit[key] = init[key];
      nextInit.body = newBody;
      return nativeFetch(input, nextInit).then(function (response) {
        invalidate();
        return response;
      });
    };

    /** A landed add flips this page's buttons for those variants, is worth announcing, and gets the cart re-checked for what a server-side merge may have raised. */
    var afterAdd = function (plan) {
      return function (response) {
        if (response && response.ok) {
          justAdded = {};
          plan.items.forEach(function (item) {
            justAdded[item.id] = true;
          });
          justAddedAt = Date.now();
          heal();
        }
        scheduleUpdate();
        return response;
      };
    };

    if (kind === "add") {
      var plan = addPlan(body);
      if (!plan) return nativeFetch.apply(null, args);
      return readCart().then(function (ids) {
        var call = verdict(plan, ids);
        if (call === "BLOCK") {
          note(IN_CART);
          return refusal();
        }
        // an add in flight counts as in the cart, so a second click landing before the first response cannot slip past
        plan.items.forEach(function (item) {
          ids.add(item.id);
        });
        if (call === "ONE") {
          note(LIMIT);
          return sendClamped(plan.toOne()).then(afterAdd(plan));
        }
        return passThrough().then(afterAdd(plan));
      });
    }

    var change = kind === "change" ? changePlan(body) : updatePlan(body);
    if (!change) return nativeFetch.apply(null, args);
    var raised = kind === "change" ? change.quantity > 1 : change.over;
    if (!raised)
      return passThrough().then(function (response) {
        scheduleUpdate();
        return response;
      });
    note(LIMIT);
    return sendClamped(change.toOne()).then(function (response) {
      scheduleUpdate();
      return response;
    });
  };

  // ---------------- the healed cart ----------------

  /* Shopify's cart transform merges an Easy Bundles add into one parent line AFTER the request passes this guard, so a second box lands as a quantity raise (or a duplicate line of the parent variant) that no request inspection can see — reading the cart back after every landed add catches every such shape. */

  var healing = false;

  function heal() {
    if (healing) return;
    healing = true;
    window.setTimeout(function () {
      nativeFetch(root() + "cart.js", {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (cart) {
          if (!cart) {
            healing = false;
            return;
          }
          var seen = {};
          var fixes = [];
          // /cart.js lists the newest line first, and of two lines carrying one variant the older one stays — the newer is the "already in your cart" attempt
          var items = (cart.items || []).slice().reverse();
          items.forEach(function (item) {
            if (seen[item.variant_id])
              fixes.push({ key: item.key, quantity: 0, message: IN_CART });
            else {
              seen[item.variant_id] = true;
              if (item.quantity > 1)
                fixes.push({ key: item.key, quantity: 1, message: LIMIT });
            }
          });
          if (!fixes.length) {
            healing = false;
            return;
          }
          note(fixes[0].message);
          var run = function (i) {
            if (i >= fixes.length) {
              healing = false;
              invalidate();
              document.dispatchEvent(new CustomEvent("cart:refresh"));
              scheduleUpdate();
              return;
            }
            nativeFetch(root() + "cart/change.js", {
              method: "POST",
              credentials: "same-origin",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                id: fixes[i].key,
                quantity: fixes[i].quantity,
              }),
            })
              .then(function () {
                run(i + 1);
              })
              .catch(function () {
                run(i + 1);
              });
          };
          run(0);
        })
        .catch(function () {
          healing = false;
        });
    }, 400); // the transform's merge is visible a beat after the add response
  }

  // ---------------- the message ----------------

  var live = null;
  var bubble = null;
  var hideTimer = null;
  var anchor = null;

  document.addEventListener(
    "pointerdown",
    function (event) {
      anchor = event.target;
    },
    true,
  );
  document.addEventListener(
    "keydown",
    function (event) {
      if (event.key === "Enter" || event.key === " ") anchor = event.target;
    },
    true,
  );

  function style() {
    if (document.getElementById("tb-opv-style")) return;
    var css =
      ".tb-opv-live{position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;border:0}" +
      ".tb-opv-note{position:fixed;z-index:2147483000;max-width:17rem;padding:.625rem .75rem;background:#111111;color:#FFFFFF;border:1px solid #FFFFFF;font-size:.8125rem;line-height:1.35;letter-spacing:.04em;text-align:left;pointer-events:none;opacity:0;transition:opacity .15s linear}" +
      ".tb-opv-note[data-on='true']{opacity:1}" +
      ".tb-opv-x svg{transform:rotate(45deg)}" +
      "@media (prefers-reduced-motion: reduce){.tb-opv-note{transition:none}}";
    var el = document.createElement("style");
    el.id = "tb-opv-style";
    el.textContent = css;
    document.head.appendChild(el);
  }

  function place(el) {
    var target =
      anchor && anchor.closest
        ? anchor.closest(
            "button,[type='submit'],a,input,select,[role='button'],.gbbProductAddButton,.gbbProductVariantAddButton",
          ) || anchor
        : null;
    var rect =
      target && target.getBoundingClientRect
        ? target.getBoundingClientRect()
        : null;
    if (!rect || (!rect.width && !rect.height)) {
      el.style.top = "1rem";
      el.style.left = "50%";
      el.style.transform = "translateX(-50%)";
      return;
    }
    el.style.transform = "none";
    var top = Math.min(
      rect.bottom + 8,
      window.innerHeight - 12 - el.offsetHeight,
    );
    var left = Math.min(
      Math.max(8, rect.left),
      window.innerWidth - 8 - el.offsetWidth,
    );
    el.style.top = Math.max(8, top) + "px";
    el.style.left = left + "px";
  }

  /** One live region and one bubble per page, both in the document before any message: a region that appears and speaks in the same breath is the classic missed announcement, and a bubble that announced on its own would say the same thing twice. */
  function install() {
    style();
    live = document.createElement("div");
    live.id = "tb-opv-live";
    live.className = "tb-opv-live";
    live.setAttribute("role", "status");
    live.setAttribute("aria-live", "polite");
    live.setAttribute("aria-atomic", "true");
    document.body.appendChild(live);
    bubble = document.createElement("div");
    bubble.id = "tb-opv-note";
    bubble.className = "tb-opv-note";
    bubble.setAttribute("aria-hidden", "true");
    document.body.appendChild(bubble);
  }

  function note(text) {
    if (!live || !bubble) install();
    bubble.textContent = text;
    bubble.setAttribute("data-on", "true");
    place(bubble);
    say(text);
    if (hideTimer) window.clearTimeout(hideTimer);
    hideTimer = window.setTimeout(function () {
      bubble.setAttribute("data-on", "false");
    }, NOTE_MS);
  }

  /** Announce without a bubble, for a change the page already shows some other way. */
  function say(text) {
    if (!live || !bubble) install();
    live.textContent = "";
    window.setTimeout(function () {
      live.textContent = text;
    }, 60); // the same text twice running is not an update, so the region is cleared first
  }

  // ---------------- the add button while its variant is in the cart ----------------

  /* An add button whose form's variant the cart holds is a working remove control — the same element mutated in place (focus never moves, the accessible name follows the visible state), with an icon-only control (a card's quick-add plus) keeping its icon rotated into an X and renaming its sr-only label instead. */

  var originals = typeof WeakMap === "function" ? new WeakMap() : null;
  var justAdded = null; // {variantId: true} for the add this page just sent, so only that flip is announced
  var justAddedAt = 0;
  var updateTimer = null;

  function variantOf(form) {
    var input = form.querySelector('[name="id"]');
    return input && input.value ? String(input.value) : null;
  }

  /** The form's submit buttons, external `form=`-associated ones included. */
  function buttonsOf(form) {
    var out = [];
    var els = form.elements;
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.tagName === "BUTTON" && el.type === "submit") out.push(el);
    }
    return out;
  }

  function flip(btn, variantId) {
    if (btn.getAttribute("data-tb-opv") === "remove") {
      btn.setAttribute("data-tb-opv-variant", variantId);
      return false;
    }
    if (!originals) return false;
    originals.set(btn, btn.innerHTML);
    var sr = btn.querySelector(".sr-only");
    if (sr && sr.textContent.trim() === btn.textContent.trim()) {
      sr.textContent = REMOVE_LABEL;
      btn.classList.add("tb-opv-x");
    } else {
      btn.textContent = REMOVE_LABEL;
    }
    btn.setAttribute("data-tb-opv", "remove");
    btn.setAttribute("data-tb-opv-variant", variantId);
    return true;
  }

  function unflip(btn) {
    if (btn.getAttribute("data-tb-opv") !== "remove") return;
    var orig = originals && originals.get(btn);
    if (orig != null) btn.innerHTML = orig;
    btn.classList.remove("tb-opv-x");
    btn.removeAttribute("data-tb-opv");
    btn.removeAttribute("data-tb-opv-variant");
  }

  function updateButtons() {
    readCart().then(function (ids) {
      var forms = document.querySelectorAll('form[action*="/cart/add"]');
      var announce = false;
      for (var i = 0; i < forms.length; i++) {
        var variant = variantOf(forms[i]);
        if (!variant) continue;
        var btns = buttonsOf(forms[i]);
        for (var j = 0; j < btns.length; j++) {
          if (ids.has(variant)) {
            var flippedNow = flip(btns[j], variant);
            if (
              flippedNow &&
              justAdded &&
              justAdded[variant] &&
              Date.now() - justAddedAt < 8000
            )
              announce = true;
          } else unflip(btns[j]);
        }
      }
      if (announce) {
        justAdded = null;
        say(ADDED_MSG);
      }
    });
  }

  function scheduleUpdate() {
    if (updateTimer) window.clearTimeout(updateTimer);
    updateTimer = window.setTimeout(function () {
      updateTimer = null;
      updateButtons();
    }, 200);
  }

  /** Capture-phase, so the theme's own submit path never fires for a control in its remove state. */
  document.addEventListener(
    "click",
    function (event) {
      var target = event.target;
      var btn =
        target && target.closest ? target.closest('[data-tb-opv="remove"]') : null;
      if (!btn) return;
      event.preventDefault();
      if (btn.getAttribute("aria-busy") === "true") return;
      var variant = btn.getAttribute("data-tb-opv-variant");
      if (!variant) return;
      btn.setAttribute("aria-busy", "true");
      var updates = {};
      updates[variant] = 0;
      window
        .fetch(root() + "cart/update.js", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates: updates }),
        })
        .then(function (response) {
          btn.removeAttribute("aria-busy");
          invalidate();
          if (response && response.ok) {
            note(REMOVED_MSG);
            // the theme's cart pieces (drawer, count, dot) re-read the cart on this event
            document.dispatchEvent(new CustomEvent("cart:refresh"));
          }
          updateButtons();
        })
        .catch(function () {
          btn.removeAttribute("aria-busy");
          updateButtons();
        });
    },
    true,
  );

  /** New or replaced markup (a quick-view modal, a re-rendered product form) gets the current state; the flip itself is idempotent, so the observer settles. */
  function watchDom() {
    if (typeof MutationObserver !== "function") return;
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (!m.addedNodes.length && !m.removedNodes.length) continue;
        var t = m.target;
        if (t && t.id && (t.id === "tb-opv-note" || t.id === "tb-opv-live"))
          continue;
        scheduleUpdate();
        return;
      }
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  function start() {
    install();
    updateButtons();
    watchDom();
    heal(); // a cart can arrive already raised — a permalink add, a hand-edited change URL — with no add for the post-add heal to ride
  }

  if (document.body) start();
  else document.addEventListener("DOMContentLoaded", start);
  readCart();
  // a variant switch rewrites the form's id input without a DOM mutation on some themes
  document.addEventListener("change", scheduleUpdate, true);
  document.addEventListener("cart:change", scheduleUpdate);
  document.addEventListener("cart:refresh", scheduleUpdate);
  window.addEventListener("pageshow", function () {
    invalidate();
    scheduleUpdate();
  });
  document.addEventListener("visibilitychange", function () {
    if (!document.hidden) {
      invalidate();
      scheduleUpdate();
    }
  });
})();
